/**
 * PWA 推播訂閱服務
 */
import { http } from "../utils/http";

// 實際的公鑰由環境變數注入
// const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;


/** `navigator.serviceWorker.ready` 的逾時（SW 註冊失敗時它不會 reject，只是永遠 pending） */
const SW_READY_TIMEOUT_MS = 5000;
/** `pushManager.subscribe()` 的逾時（連不到 push service 時可能長時間不回應） */
const SUBSCRIBE_TIMEOUT_MS = 15000;

/**
 * 這些 Web Push API 失敗時未必會 reject，可能就這樣一直 pending，
 * 讓呼叫端的按鈕永遠轉圈。一律加逾時，寧可明確報錯讓使用者知道要重試。
 */
const withTimeout = (promise, ms, code) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => {
        const err = new Error(`推播操作逾時（${code}）`);
        err.code = code;
        reject(err);
      }, ms)
    ),
  ]);

const swReady = () =>
  withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS, "SW_TIMEOUT");

/**
 * 檢查瀏覽器是否支援推播。
 * 必須一併檢查 Notification —— iOS 非 standalone 的 Safari 沒有這個物件，
 * 少了這道檢查，後續讀 Notification.permission 會直接 throw。
 */
export const isPushSupported = () => {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
};

/**
 * 要求通知權限。
 * 回傳原始的權限值而非布林 —— "denied"（使用者按封鎖）與 "default"（把詢問框關掉）
 * 的後續處理不同：前者要引導去系統設定，後者只需請他再按一次。
 *
 * @returns {Promise<"granted" | "denied" | "default">}
 */
export const requestNotificationPermission = async () => {
  return await Notification.requestPermission();
};

/**
 * 取得這台裝置目前的推播訂閱，沒有則為 null。
 * 供狀態推導、測試發送、解除綁定共用。
 */
export const getCurrentSubscription = async () => {
  const registration = await swReady();
  return await registration.pushManager.getSubscription();
};

/**
 * 將 Base64 URL 安全字串轉為 Uint8Array (VAPID Key 格式轉換)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * 訂閱推播
 */
export const subscribeUserToPush = async () => {
  try {
    const registration = await swReady();

    // 檢查是否已有訂閱
    let subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      return subscription;
    }

    // 發起新訂閱
    const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      // 帶 code 讓 UI 能顯示明確原因，不要被泛用的「綁定失敗」蓋掉（環境設定錯誤時查不到原因）
      const err = new Error("系統未配置推播公鑰 (VITE_VAPID_PUBLIC_KEY)，無法啟動推播服務。");
      err.code = "NO_VAPID_KEY";
      throw err;
    }

    subscription = await withTimeout(
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      }),
      SUBSCRIBE_TIMEOUT_MS,
      "SUBSCRIBE_TIMEOUT"
    );

    console.log("訂閱成功:", JSON.stringify(subscription));
    return subscription;
  } catch (error) {
    console.error("訂閱推播失敗:", error);
    throw error;
  }
};

/**
 * 綁定推播裝置
 * POST /push/subscription
 * body 即 PushSubscription.toJSON()：{ endpoint, keys: { p256dh, auth } }
 * 後端以 endpoint upsert，client_id / user_id 由 token 解出。
 *
 * @param {PushSubscription} subscription
 */
export const savePushSubscription = async (subscription) => {
  await http.post("/push/subscription", subscription.toJSON());
};

/**
 * 解除綁定推播裝置（等冪，找不到也回成功）
 * DELETE /push/subscription
 *
 * @param {string} endpoint
 */
export const deletePushSubscription = async (endpoint) => {
  await http.delete("/push/subscription", { data: { endpoint } });
};

/**
 * 「這台裝置曾開啟推播、且使用者沒有自己關掉」的標記。
 * 用來分辨「從沒開過」與「開過但被手機收回 / 訂閱失效」：
 * 後者權限還在時可靜默重訂，權限沒了則提示「被關閉了」而非「尚未開啟」。
 * 綁定 / 登入同步成功時設，設定頁手動取消綁定時清。
 */
const WAS_BOUND_KEY = "push_was_bound";

/** 推播狀態在畫面外被改變（登入同步靜默重訂）時發的 window 事件，usePushStatus 收到即重算 */
export const PUSH_STATUS_CHANGED = "push-status-changed";

export const wasBound = () => {
  try {
    return localStorage.getItem(WAS_BOUND_KEY) === "1";
  } catch {
    return false;
  }
};

export const setWasBound = (value) => {
  try {
    if (value) localStorage.setItem(WAS_BOUND_KEY, "1");
    else localStorage.removeItem(WAS_BOUND_KEY);
  } catch {
    // 無痕或儲存被封鎖：只是少了「被關閉」的判斷，不影響綁定本身
  }
};

/**
 * 綁定這台裝置的完整流程：要權限 → 訂閱 → 送後端，後端失敗則退訂回滾
 * （避免「本機有訂閱但後端沒門牌」的假綁定）。
 * 設定頁與首頁引導卡共用，兩處的回滾行為才不會走偏。
 *
 * 注意：必須由使用者手勢觸發（iOS / Firefox 強制要求）。
 *
 * @returns {Promise<"bound" | "denied" | "dismissed">}
 *   `denied` = 使用者按封鎖；`dismissed` = 把詢問框關掉、沒做選擇（兩者提示不同）。
 *   拋錯時 `e.code` 可能為 NO_VAPID_KEY / SW_TIMEOUT / SUBSCRIBE_TIMEOUT。
 */
export const bindThisDevice = async () => {
  const permission = await requestNotificationPermission();
  if (permission !== "granted") {
    return permission === "denied" ? "denied" : "dismissed";
  }

  const sub = await subscribeUserToPush();
  try {
    await savePushSubscription(sub);
  } catch (e) {
    await sub.unsubscribe();
    throw e;
  }
  setWasBound(true);
  return "bound";
};

/**
 * 手動登出時解除這台裝置的綁定。
 *
 * **只打後端 DELETE，不呼叫 `unsubscribe()`** —— 保留瀏覽器訂閱，
 * 重新登入時才能靜默自動重綁、使用者零操作（設計文件 §3）。
 *
 * 失敗絕不可擋住登出，一律吞掉不重試：最差只是退化成被動登出的行為，而那本來就可接受。
 * `getCurrentSubscription()` 已有 SW 逾時保護，不會卡住登出流程。
 */
export const unbindDeviceForLogout = async () => {
  try {
    const sub = await getCurrentSubscription();
    if (sub) await deletePushSubscription(sub.endpoint);
  } catch (e) {
    console.error("登出解綁失敗，略過", e);
  }
};

/**
 * 發送測試推播
 * POST /push/test
 * 一律帶 endpoint，只發給當前這台裝置 —— 否則同帳號的其他裝置也會一起響。
 * 尚未綁定 / 門牌失效時回 422 + error_code "PARAM-008"。
 *
 * @param {string} endpoint
 */
export const sendPushTest = async (endpoint) => {
  await http.post("/push/test", { endpoint });
};
